import {useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import api from "../api/api";
import '../styles/Blogs.css';
import {IMAGE_URL} from "../path";
import Header from "./Components/Header";
import FooterWithSignup from "./Components/FooterWithSignup";
import cartonBackground from "../assets/images/carton-background.jpg";
import SEO from "./Components/SEO";
import { decode } from "html-entities";

type Pagination = {
    currentPage: number;
    lastPage: number;
    total: number;
};

type Category = {
    id: number;
    name: string;
    slug: string;
};

function Blogs() {
    const { slug } = useParams();

    const [loading, setLoading] = useState(true);

    const [categories, setCategories] = useState<Category[]>([]);

    const [selectedCategory, setSelectedCategory] = useState("");

    const [blogs, setBlogs] = useState([]);

    const selectedCategoryData = categories.find(
        (category: any) => category.slug === selectedCategory
    );

    const stripHtml = (text?: string) =>
        text
            ? decode(decode(text))
                .replace(/<[^>]*>/g, "")
                .replace(/\s+/g, " ")
                .trim()
            : undefined;


    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        lastPage: 1,
        total: 0,
    });

    const [coverImage, setCoverImage] = useState("");

    const [headerBody, setHeaderBody] = useState("");

    useEffect(() => {

        fetchBlogs();

    }, [slug]);


    const fetchBlogs = async (page = 1) => {
        const endpoint =
            !slug || slug === "all"
                ? `/fetch-blogs?page=${page}`
                : `/fetch-blogs/${slug}?page=${page}`;

        const response = await api.get(endpoint);

        setCategories(response.data.categories);
        setSelectedCategory(response.data.selected_category?.slug ?? "all");
        setBlogs(response.data.blogs.data);

        setPagination({
            currentPage: response.data.blogs.current_page,
            lastPage: response.data.blogs.last_page,
            total: response.data.blogs.total,
        });

        setCoverImage(response.data.cover_image);
        setHeaderBody(response.data.header_body);
    };

    const changePage = (
        page: number
    ) => {

        fetchBlogs(page);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });

    };

    const navigate = useNavigate();

    const changeCategory = (
        categorySlug: string
    ) => {

        if (
            categorySlug === "all"
        ) {

            navigate("/blogs");

            return;

        }

        navigate(
            `/blogs/${categorySlug}`
        );

    };

    return (
        <div>
            <SEO
                title={
                    selectedCategoryData?.slug && selectedCategoryData.slug !== "all"
                        ? `${selectedCategoryData.name} Blogs | DENNY`
                        : "Food For Thought | DENNY"
                }
                description={
                    stripHtml(headerBody) ??
                    "Explore Food For Thought by Denny for recipes, cooking tips, product inspiration and stories to help you savour every meal."
                }
                keywords={
                    selectedCategoryData?.slug && selectedCategoryData.slug !== "all"
                        ? `${selectedCategoryData.name}, ${selectedCategoryData.name} blogs, Denny blogs, food blog, cooking inspiration, recipes, Denny South Africa`
                        : "Denny blogs, Food For Thought, food blog, recipes, cooking inspiration, meal ideas, kitchen tips, Denny South Africa"
                }
            />
            <div className="blogs-header"
                 style={{
                     backgroundImage: coverImage
                         ? `url(${IMAGE_URL}${coverImage})`
                         : `url(${cartonBackground})`,
                 }}>
                <Header />
                <div className="blogs-header-inner">
                    <h1>Food For Thought</h1>
                    <p>{headerBody}</p>
                </div>
            </div>
            <div className="blogs-container">
                <div className="blogs-categories">
                    {categories.map((category: any, index: number) => (
                        <Link
                            key={index}
                            to={category.slug === "all" ? "/blogs" : `/blogs/${category.slug}`}
                            className={`category-button ${
                                ((!slug && category.slug === "all") || slug === category.slug)
                                    ? "active"
                                    : ""
                            }`}
                        >
                            {category.name}
                        </Link>
                    ))}
                </div>
                <div className="blogs-body">
                    {blogs?.map((blog: any, index: number) => (
                        <div key={index} className="blog">
                            <div className="blog-image-wrapper">
                                <img
                                    alt={blog.slug}
                                    src={`${IMAGE_URL}${blog.cover_image}`}
                                />
                            </div>
                            <div className="blog-intro-inner">
                                <h2>{blog.title}</h2>
                                <p>{blog.subtitle}</p>

                                <Link
                                    to={`/blog/${blog.slug}`}
                                    className="blog-button"
                                >
                                    Read More
                                </Link>
                            </div>


                        </div>
                    ))}
                </div>
                <div className="blogs-navigation">

                    <button
                        className="blogs-navigation-arrow"
                        onClick={() => changePage(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25">
                            <g id="Group_918" data-name="Group 918" transform="translate(-792 -942)">
                                <circle id="Ellipse_57" data-name="Ellipse 57" cx="12.5" cy="12.5" r="12.5" transform="translate(792 942)" fill="#436ab3"/>
                                <path id="Path_14050" data-name="Path 14050" d="M13.25,4.5l-5,5,5,5" transform="translate(792.75 945)" fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                            </g>
                        </svg>
                    </button>

                    <div className="blogs-navigation-dots">

                        {Array.from(
                            { length: pagination.lastPage },
                            (_, index) => {

                                const page = index + 1;

                                return (
                                    <button
                                        key={page}
                                        className={`blogs-navigation-dot ${
                                            pagination.currentPage === page
                                                ? "active"
                                                : ""
                                        }`}
                                        onClick={() => changePage(page)}
                                    />
                                );
                            }
                        )}


                    </div>

                    <button
                        className="blogs-navigation-arrow"
                        onClick={() => changePage(pagination.currentPage + 1)}
                        disabled={
                            pagination.currentPage ===
                            pagination.lastPage
                        }
                    >
                        <svg id="Group_917" data-name="Group 917" xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25">
                            <circle id="Ellipse_57" data-name="Ellipse 57" cx="12.5" cy="12.5" r="12.5" fill="#436ab3"/>
                            <path id="Path_14050" data-name="Path 14050" d="M8.25,4.5l5,5-5,5" transform="translate(2.75 3)" fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                        </svg>

                    </button>

                </div>
            </div>

            <FooterWithSignup />
        </div>
    )

}

export default Blogs;
